import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  CITA_SLOT_HOURS,
  slotToMinutes,
  getCitaFinDate,
  getCitaPacienteLabel,
  isCitaPacientePendiente,
  getMiembroNombre,
  intervalsOverlap,
  findCitasBlockingSlotRange,
  getCitaSlotRange,
  ESTADO_CITA_COLOR,
  ESTADO_CITA_LABEL,
} from '../../utils/citasHelpers';

const ESTADO_COLOR = ESTADO_CITA_COLOR;
const ESTADO_LABEL = ESTADO_CITA_LABEL;

function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function DayScheduleCitaCard({
  cita,
  slotFrom,
  slotSpan,
  pacientes,
  equipo,
  highlightPacienteId,
  excludeCitaId,
  pickRangeMode,
  onEditCita,
  onChangeEstado,
}) {
  const isPatient =
    highlightPacienteId && String(cita.idPaciente) === String(highlightPacienteId);
  const isEditing = excludeCitaId && String(cita.id) === String(excludeCitaId);

  return (
    <div
      role="presentation"
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
      className={[
        'day-slot-cita',
        'day-slot-cita--span',
        `day-slot-cita--${cita.estado || 'pendiente'}`,
        isPatient ? 'day-slot-cita--highlight' : '',
        isEditing ? 'day-slot-cita--editing' : '',
      ].filter(Boolean).join(' ')}
      style={{
        top: `calc(${slotFrom} * var(--day-slot-row-h))`,
        height: `calc(${slotSpan} * var(--day-slot-row-h) - 4px)`,
      }}
    >
      <div className="day-slot-cita-header">
        <span className="day-slot-cita-hora">
          {new Date(cita.fechaHora).toLocaleTimeString('es-EC', { hour: '2-digit', minute: '2-digit' })}
          {' – '}
          {getCitaFinDate(cita).toLocaleTimeString('es-EC', { hour: '2-digit', minute: '2-digit' })}
        </span>
        <span className="day-slot-cita-doctor">{getMiembroNombre(equipo, cita.idDoctor)}</span>
        <span className="day-slot-cita-paciente">
          {getCitaPacienteLabel(cita, pacientes)}
          {isCitaPacientePendiente(cita) ? ' · nuevo' : ''}
        </span>
        <span className={`status-pill status-pill--${ESTADO_COLOR[cita.estado] || 'secondary'} ms-auto`}>
          {ESTADO_LABEL[cita.estado] || cita.estado}
        </span>
      </div>
      {cita.motivo ? <div className="day-slot-cita-motivo">{cita.motivo}</div> : null}
      {!pickRangeMode ? (
        <div className="day-slot-cita-actions">
          {onEditCita ? (
            <button
              type="button"
              className="cp-btn-action cp-btn-action--secondary"
              title="Editar o reprogramar"
              onClick={(e) => {
                e.stopPropagation();
                onEditCita(cita);
              }}
            >
              <i className="bi bi-pencil" />
            </button>
          ) : null}
          {cita.estado === 'pendiente' && onChangeEstado ? (
            <>
              <button
                type="button"
                className="cp-btn-action cp-btn-action--secondary"
                title="Confirmar"
                onClick={(e) => {
                  e.stopPropagation();
                  onChangeEstado(cita, 'confirmada');
                }}
              >
                <i className="bi bi-check-lg" />
              </button>
              <button
                type="button"
                className="cp-btn-action cp-btn-action--danger"
                title="Cancelar"
                onClick={(e) => {
                  e.stopPropagation();
                  onChangeEstado(cita, 'cancelada');
                }}
              >
                <i className="bi bi-x-lg" />
              </button>
            </>
          ) : null}
          {cita.estado === 'confirmada' && onChangeEstado ? (
            <button
              type="button"
              className="cp-btn-action cp-btn-action--secondary"
              title="Marcar realizada"
              onClick={(e) => {
                e.stopPropagation();
                onChangeEstado(cita, 'realizada');
              }}
            >
              <i className="bi bi-clipboard2-check" />
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Grilla horaria del día (arrastrar / clic en franjas). Reutilizable en modal o panel lateral.
 */
export default function DayScheduleContent({
  date,
  onDateChange,
  citas = [],
  pacientes = [],
  equipo = [],
  onNewCita,
  onChangeEstado,
  onEditCita,
  schedulingHint,
  highlightPacienteId,
  closeOnSlotSelect = true,
  onBeforeSlotSelect,
  compactHeader = false,
  pickRangeMode = false,
  onPickRange,
  onPickRangeBlocked,
  excludeCitaId = null,
  formSelectedRange = null,
  embedInForm = false,
  hideHeader = false,
}) {
  const anchorIdxRef = useRef(null);
  const hoverIdxRef = useRef(null);
  const mouseUpHandlerRef = useRef(null);
  const [rangeSel, setRangeSel] = useState(null);

  const dayDate = date ? startOfDay(date) : null;

  const citasDelDia = useMemo(() => {
    if (!dayDate) return [];
    return citas
      .filter((c) => new Date(c.fechaHora).toDateString() === dayDate.toDateString())
      .sort((a, b) => new Date(a.fechaHora) - new Date(b.fechaHora));
  }, [citas, dayDate]);

  const citasPorSlot = useMemo(() => {
    if (!dayDate) return {};
    const map = {};
    CITA_SLOT_HOURS.forEach((slot) => {
      map[slot] = [];
      const slotStart = slotToMinutes(slot);
      const slotEnd = slotStart + 30;
      citasDelDia.forEach((c) => {
        const cStart = new Date(c.fechaHora);
        const cEnd = getCitaFinDate(c);
        const c0 = cStart.getHours() * 60 + cStart.getMinutes();
        const c1 = cEnd.getHours() * 60 + cEnd.getMinutes();
        if (intervalsOverlap(slotStart, slotEnd, c0, c1)) {
          map[slot].push(c);
        }
      });
    });
    return map;
  }, [citasDelDia, dayDate]);

  const activasCount = citasDelDia.filter((c) => c.estado !== 'cancelada').length;

  const citasOverlay = useMemo(() => {
    if (!dayDate) return [];
    let list = citasDelDia;
    if (pickRangeMode && excludeCitaId) {
      list = list.filter((c) => String(c.id) !== String(excludeCitaId));
    }
    return list
      .map((cita) => {
        const range = getCitaSlotRange(cita, dayDate);
        if (!range) return null;
        return {
          cita,
          slotFrom: range.from,
          slotSpan: range.to - range.from + 1,
        };
      })
      .filter(Boolean);
  }, [citasDelDia, dayDate, pickRangeMode, excludeCitaId]);

  const clearDragListeners = useCallback(() => {
    if (mouseUpHandlerRef.current) {
      window.removeEventListener('mouseup', mouseUpHandlerRef.current, true);
      mouseUpHandlerRef.current = null;
    }
  }, []);

  useEffect(() => () => clearDragListeners(), [clearDragListeners]);

  const finishSlotSelection = useCallback(() => {
    const a = anchorIdxRef.current;
    if (a === null || !dayDate) return;
    const h = hoverIdxRef.current ?? a;
    anchorIdxRef.current = null;
    hoverIdxRef.current = null;
    setRangeSel(null);
    const from = Math.min(a, h);
    const to = Math.max(a, h);
    if (closeOnSlotSelect) onBeforeSlotSelect?.();
    if (pickRangeMode) {
      const blocking = findCitasBlockingSlotRange(citas, dayDate, from, to, excludeCitaId);
      if (blocking.length > 0) {
        onPickRangeBlocked?.(blocking, dayDate, from, to);
        return;
      }
      if (from === to) {
        onPickRange?.(dayDate, CITA_SLOT_HOURS[from]);
      } else {
        onPickRange?.(dayDate, CITA_SLOT_HOURS[from], CITA_SLOT_HOURS[to]);
      }
      return;
    }
    if (from === to) {
      onNewCita?.(dayDate, CITA_SLOT_HOURS[from]);
    } else {
      onNewCita?.(dayDate, CITA_SLOT_HOURS[from], CITA_SLOT_HOURS[to]);
    }
  }, [dayDate, closeOnSlotSelect, onBeforeSlotSelect, onNewCita, pickRangeMode, onPickRange, onPickRangeBlocked, citas, excludeCitaId]);

  const handleSlotMouseDown = (e, idx) => {
    if (e.button !== 0) return;
    if (pickRangeMode && findCitasBlockingSlotRange(citas, dayDate, idx, idx, excludeCitaId).length > 0) {
      return;
    }
    e.preventDefault();
    clearDragListeners();
    anchorIdxRef.current = idx;
    hoverIdxRef.current = idx;
    setRangeSel({ from: idx, to: idx });
    const onUp = () => {
      clearDragListeners();
      finishSlotSelection();
    };
    mouseUpHandlerRef.current = onUp;
    window.addEventListener('mouseup', onUp, true);
  };

  const handleSlotMouseEnter = (idx) => {
    if (anchorIdxRef.current === null) return;
    const a = anchorIdxRef.current;
    hoverIdxRef.current = idx;
    setRangeSel({ from: Math.min(a, idx), to: Math.max(a, idx) });
  };

  const handleSlotKeyDown = (e, idx) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      anchorIdxRef.current = null;
      hoverIdxRef.current = null;
      clearDragListeners();
      setRangeSel(null);
      if (closeOnSlotSelect) onBeforeSlotSelect?.();
      if (pickRangeMode) {
        onPickRange?.(dayDate, CITA_SLOT_HOURS[idx]);
      } else {
        onNewCita?.(dayDate, CITA_SLOT_HOURS[idx]);
      }
    }
  };

  const shiftDay = (delta) => {
    if (!dayDate || !onDateChange) return;
    const next = new Date(dayDate);
    next.setDate(next.getDate() + delta);
    onDateChange(next);
  };

  const goToday = () => {
    if (!onDateChange) return;
    onDateChange(startOfDay(new Date()));
  };

  if (!dayDate) return null;

  const isToday = dayDate.toDateString() === new Date().toDateString();

  const dayTitleOptions = embedInForm
    ? { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }
    : { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' };

  return (
    <div
      className={[
        'day-schedule-content',
        compactHeader ? 'day-schedule-content--compact' : '',
        embedInForm ? 'day-schedule-content--embed-form' : '',
        pickRangeMode ? 'day-schedule-content--pick-range' : '',
      ].filter(Boolean).join(' ')}
    >
      {!hideHeader ? (
      <div className={`day-schedule-content__head${compactHeader ? ' day-schedule-content__head--compact' : ''}`}>
        <div className="day-schedule-modal__title-wrap">
          <h3 className="day-schedule-content__title">
            <i className="bi bi-calendar-day" aria-hidden />
            {dayDate.toLocaleDateString('es-EC', dayTitleOptions)}
          </h3>
          {schedulingHint && !embedInForm ? (
            <p className="day-schedule-modal__hint mb-0">{schedulingHint}</p>
          ) : null}
        </div>
        {onDateChange ? (
          <div className="day-schedule-modal__nav" role="group" aria-label="Cambiar día">
            <button type="button" className="day-schedule-nav-btn" onClick={() => shiftDay(-1)} title="Día anterior">
              <i className="bi bi-chevron-left" />
            </button>
            <button
              type="button"
              className={`day-schedule-nav-btn day-schedule-nav-btn--today${isToday ? ' is-active' : ''}`}
              onClick={goToday}
              disabled={isToday}
            >
              Hoy
            </button>
            <button type="button" className="day-schedule-nav-btn" onClick={() => shiftDay(1)} title="Día siguiente">
              <i className="bi bi-chevron-right" />
            </button>
          </div>
        ) : null}
      </div>
      ) : null}

      {!embedInForm ? (
        <div className="day-schedule-summary">
          <div className="day-schedule-summary-stat">
            <span className="day-sched-num">{citasDelDia.length}</span>
            <span className="day-sched-label">Total citas</span>
          </div>
          <div className="day-schedule-summary-stat">
            <span className="day-sched-num" style={{ color: '#13DEB9' }}>{activasCount}</span>
            <span className="day-sched-label">Activas</span>
          </div>
          <div className="day-schedule-summary-stat">
            <span className="day-sched-num" style={{ color: '#FA896B' }}>
              {citasDelDia.filter((c) => c.estado === 'cancelada').length}
            </span>
            <span className="day-sched-label">Canceladas</span>
          </div>
          {!pickRangeMode ? (
            <button
              type="button"
              className="cp-btn cp-btn--primary cp-btn--sm ms-auto"
              onClick={() => {
                anchorIdxRef.current = null;
                clearDragListeners();
                if (closeOnSlotSelect) onBeforeSlotSelect?.();
                onNewCita?.(dayDate);
              }}
            >
              <i className="bi bi-plus-lg" /> Sin elegir hora
            </button>
          ) : null}
        </div>
      ) : null}

      {!(embedInForm && pickRangeMode) ? (
      <p className="day-schedule-drag-hint">
        <i className="bi bi-arrows-expand" aria-hidden />
        <span>
          {pickRangeMode ? (
            <>
              <strong>Arrastrá</strong> a una franja libre para <strong>mover</strong> la cita. Las canceladas liberan horario.
              No podés ocupar un bloque con otra cita activa.
            </>
          ) : (
            <>
              <strong>Arrastra</strong> por los bloques de horario para elegir la duración (30 min por fila), o{' '}
              <strong>haz un clic</strong> en una franja libre.
            </>
          )}
        </span>
      </p>
      ) : null}

      <div className="day-schedule-timeline">
        <div className="day-schedule-timeline__inner">
          {CITA_SLOT_HOURS.map((slot, idx) => {
          const citasSlot = citasPorSlot[slot] || [];
          const activas = citasSlot.filter((c) => c.estado !== 'cancelada');
          const activasOtras = excludeCitaId
            ? activas.filter((c) => String(c.id) !== String(excludeCitaId))
            : activas;
          const ocupado = activasOtras.length > 0;
          const tieneCanceladas = citasSlot.some((c) => c.estado === 'cancelada');
          const soloLiberado = !ocupado && tieneCanceladas && pickRangeMode;
          const inRange = rangeSel && idx >= rangeSel.from && idx <= rangeSel.to;
          const inFormRange =
            formSelectedRange && idx >= formSelectedRange.from && idx <= formSelectedRange.to;

          return (
            <div
              key={slot}
              role="button"
              tabIndex={ocupado && pickRangeMode ? -1 : 0}
              aria-disabled={ocupado && pickRangeMode ? true : undefined}
              className={[
                'day-slot',
                ocupado ? 'day-slot--busy' : 'day-slot--free',
                soloLiberado ? 'day-slot--freed' : '',
                inRange ? 'day-slot--range-select' : '',
                inFormRange ? 'day-slot--form-selected' : '',
                ocupado && pickRangeMode ? 'day-slot--blocked-move' : '',
              ].filter(Boolean).join(' ')}
              title={
                ocupado && pickRangeMode
                  ? 'Horario ocupado por otra cita activa'
                  : soloLiberado
                    ? 'Franja liberada (cita cancelada)'
                    : 'Mantén pulsado y arrastra para elegir varios bloques de 30 min, o un clic para solo uno'
              }
              onMouseDown={(e) => handleSlotMouseDown(e, idx)}
              onMouseEnter={() => handleSlotMouseEnter(idx)}
              onKeyDown={(e) => handleSlotKeyDown(e, idx)}
            >
              <div className="day-slot-time">{slot}</div>
              <div className="day-slot-content">
                {!ocupado ? (
                  <div className={`day-slot-free-label${pickRangeMode ? ' day-slot-free-label--pick' : ''}`}>
                    <i className={`bi ${soloLiberado ? 'bi-unlock' : 'bi-grip-horizontal'}`} aria-hidden />
                    {pickRangeMode
                      ? (inFormRange ? 'Nuevo horario' : soloLiberado ? 'Liberado · mover aquí' : 'Mover aquí')
                      : 'Arrastra o clic'}
                  </div>
                ) : null}
              </div>
            </div>
          );
        })}
          <div className="day-schedule-citas-layer">
            {citasOverlay.map(({ cita, slotFrom, slotSpan }) => (
              <DayScheduleCitaCard
                key={cita.id}
                cita={cita}
                slotFrom={slotFrom}
                slotSpan={slotSpan}
                pacientes={pacientes}
                equipo={equipo}
                highlightPacienteId={highlightPacienteId}
                excludeCitaId={excludeCitaId}
                pickRangeMode={pickRangeMode}
                onEditCita={onEditCita}
                onChangeEstado={onChangeEstado}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
